// USES ON: DISCOUNT CODES PAGE CREATE/EDIT/COPY MODE
// USES ON: CUSTOMER PROFILE PAGE CREATE NEW PERSONAL CODE
// USES ON: CUSTOMER PROFILE PAGE
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog } from "@mui/material";

import * as LogRocket from "logrocket";
import { Formik, FormikHelpers } from "formik";
import { useRouteMatch } from "react-router-dom";
import mt from "moment-timezone";

import { APIDiscounts } from "../../../api";
import { ISaveDiscountCodes } from "../../../api/discounts/index.types";
import { userDiscountActions } from "../../../reducers/userDiscount";
import {
  useAdminProviderDataContext,
  useAuthContext,
  useSearchCustomersContext,
} from "../../../context";

import {
  DISCOUNT_CODE_VALIDATION_SCHEMA,
  getPlanId,
  getQty,
  getSuffix,
  INITIAL_CODE,
} from "./utils";

import Snackbar, { TTypeMessage } from "../Snackbar";

import { ICode, IProps } from "./types";
import { discountCodesActions } from "../../../reducers/discountCodes";

import { getCustomer } from "../../../pages/Customers/utils";
import DiscountForm from "./DiscountForm";
import { customersActions } from "../../../reducers/customers";
/* eslint-disable no-template-curly-in-string */
const EMAIL_PATTERN = "${email}";
/* eslint-disable no-template-curly-in-string */
const ID_PATTERN = "${id}";

const DiscountsModal: React.FC<IProps> = ({
  isOpen,
  setIsOpen,
  getDiscount,
  prefilledEmail,
  id,
  isEditingDiscountCode,
  selectedDiscountCode,
}) => {
  const matchCustomersPage = useRouteMatch("/customers");
  const dispatch = useDispatch();

  const { hasExpired } = useAuthContext();
  const { user } = useAdminProviderDataContext();
  const searchCustomersContext = useSearchCustomersContext();

  const customers = useSelector((state) => state.customers.data);
  const { create_active_code } = user.allowed_features;

  const [open, setOpen] = useState<boolean>(false);
  const [status, setStatus] = useState<TTypeMessage>("");
  const [isSavingCode, setIsSavingCode] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [isOpenDownloadCSVDialog, setIsOpenDownloadCSVDialog] = useState(false);

  const CODE_INITIAL_STATE: ICode = {
    ...INITIAL_CODE,
    ...(id && { accounts_id: id }),
    ...(matchCustomersPage && {
      suffix: getSuffix(customers.count.toString()),
    }),
    active: create_active_code,
    isBatchCreate: false,
    ...(selectedDiscountCode?.code &&
      isOpen && {
        ...selectedDiscountCode,
        code: isEditingDiscountCode ? selectedDiscountCode?.code : "",
        descr: selectedDiscountCode.description || "",
        plan: getPlanId(!!selectedDiscountCode.reset, selectedDiscountCode.plan),
        // qty: getQty(selectedDiscountCode.qty),
        qty: 0,
        expires: selectedDiscountCode.expires || null,
        autoExpires: !!selectedDiscountCode.expires || false,
        percent: selectedDiscountCode.type === "percent",
        amount: +selectedDiscountCode.amt,
        autoExpiration: !!selectedDiscountCode.expiration || false,
        ...(selectedDiscountCode.expiration && {
          use_expiration: true,
          one_time: true,
        }),
        ...(selectedDiscountCode.expiration &&
          !isNaN(parseInt(selectedDiscountCode.expiration)) && {
            use_expiration_days: true,
            expiration_days: parseInt(selectedDiscountCode.expiration),
            expiration: null,
          }),
        ...(selectedDiscountCode.failover_type && {
          use_failover_amt: true,
          use_failover_type: true,
        }),
        fixed_reset: selectedDiscountCode.fixed_reset || null,
      }),
  };

  const bulkAssignEmailsArray = (code: ICode) =>
    code?.bulkAssignEmails?.replace(/[\n,]/g, " ").trim().replaceAll("  ", " ").split(" ");

  const saveCodes = async (values: ICode, formHelpers: FormikHelpers<ICode>) => {
    setIsSavingCode(true);

    const { use_expiration_days, failover_type, failover_amt } = values;

    if (matchCustomersPage) {
      const getDescription = (email: string, id: number) =>
        values.descr.replace(EMAIL_PATTERN, email).replace(ID_PATTERN, id.toString());
      const codes: ISaveDiscountCodes[] = [];
      const isContainsPatternString =
        values.descr.includes(EMAIL_PATTERN) || values.descr.includes(ID_PATTERN);

      customers.allAccounts.forEach(({ id, email }, index) => {
        codes.push({
          code: `${values.code}${values.suffix[index]}`,
          accounts_id: id,
          desc: isContainsPatternString ? getDescription(email, id) : values.descr,
          amt: values.amount,
          qty: getQty(values.qty),
          plan_id: values.plan === -2 ? -1 : values.plan,
          type: values.percent ? "percent" : "flat",
          one_time: !values.one_time,
          active: values.active,
          expires: values.expires,
          reset: values.isReset || values.plan === -2,
          editableValueKeyName: "create_code",
          expiration: use_expiration_days
            ? values.expiration_days.toString()
            : values.expiration
            ? `${mt(values.expiration).format("MMM D")} 00:00:00 ${mt()
                .tz("America/Chicago")
                .format("zz")} ${mt(values.expiration).format("Y")}`
            : null,
          failover_type,
          failover_amt,
          fixed_reset: values.fixed_reset,
        });
      });

      try {
        const discountCodesResponse = await APIDiscounts.createBatchCodesRequest({
          codes,
          editableValueKeyName: "batch_create_code",
        });
        LogRocket.track("Discounts: Create");
        setIsOpenDownloadCSVDialog(true);
        setOpen(true);
        const responseData = discountCodesResponse.data.data;
        dispatch(discountCodesActions.fetchSuccess(responseData));
      } catch (error) {
        setStatus("error");
        setOpen(true);
      } finally {
        setIsSavingCode(false);
        formHelpers.resetForm();
      }
      return;
    }

    const data = {
      ...(isEditingDiscountCode && { code_id: selectedDiscountCode?.id }),
      code: values.suffix.length > 0 ? values.suffix.map((s) => values.code + s) : values.code,
      amt: values.amount,
      desc: values.descr,
      qty: bulkAssignEmailsArray(values)?.length || values.qty,
      plan_id: values.plan === -2 || values.plan === -1 ? null : values.plan,
      type: values.percent ? "percent" : "flat",
      one_time: values.one_time,
      active: values.active,
      expires: values.expires,
      accounts_id: values.accounts_id,
      reset: values.plan === -2 ? true : null,
      editableValueKeyName: isEditingDiscountCode ? "update_code" : "create_code",
      bulkAssignEmails: bulkAssignEmailsArray,
      expiration: use_expiration_days
        ? values.expiration_days.toString()
        : values.expiration
        ? `${mt(values.expiration).format("MMM D")} 00:00:00 ${mt()
            .tz("America/Chicago")
            .format("zz")} ${mt(values.expiration).format("Y")}`
        : null,

      failover_type,
      failover_amt,
      fixed_reset: values.fixed_reset,
    };

    try {
      if (isEditingDiscountCode) {
        await APIDiscounts.updateDiscountCodeRequest(data);
        LogRocket.track("Update: Create");
      } else {
        await APIDiscounts.saveDiscountCodesRequest(data);
        LogRocket.track("Discounts: Create");
      }

      setStatus("success");
      setOpen(true);
      setIsOpen(false);
      getDiscount && getDiscount();

      if (prefilledEmail && id) {
        const response = await APIDiscounts.getCustomerDiscountCodesRequest({
          id,
          limit: 20,
          page: 0,
        });
        dispatch(userDiscountActions.fetchSuccess({ discountCodes: response.data, id }));
      }
    } catch (error) {
      const e = error as { response: { data: { message: string } } };
      setErrorMessage(
        e?.response?.data?.message || "Oops, something went wrong. Please reload the page !"
      );
      setStatus("error");
      setOpen(true);
    } finally {
      formHelpers.resetForm();
    }
    setIsSavingCode(false);
  };

  const handleCloseDialog = () => {
    setIsOpen(false);
    setIsOpenDownloadCSVDialog(false);
  };

  const handleCloseDownloadCsvDialog = (cb: () => void) => {
    cb();
    handleCloseDialog();
  };

  const handleCloseSnackBar = () => {
    setOpen(false);
    setErrorMessage("");
  };

  useEffect(() => {
    if (matchCustomersPage) {
      try {
        const controller = new AbortController();
        const { signal } = controller;

        dispatch(customersActions.fetchAllCustomersRequest());
        const customersData = async () => {
          const response = await getCustomer(
            searchCustomersContext,
            customers.count,
            hasExpired,
            customers,
            true,
            dispatch,
            signal,
            false
          );
          if (response) {
            dispatch(customersActions.fetchAllCustomersSuccess({ customers: response.accounts }));
          }
        };
        if (isOpen) {
          customersData();
        }
      } catch (error) {
        dispatch(customersActions.fetchAllCustomersFailed());
      }
    }
  }, [isOpen]);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleCloseDialog}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <Formik
          enableReinitialize
          validateOnChange
          validateOnMount
          validateOnBlur
          initialValues={CODE_INITIAL_STATE}
          onSubmit={saveCodes}
          validationSchema={DISCOUNT_CODE_VALIDATION_SCHEMA}
        >
          {(formikProps) => {
            return (
              <DiscountForm
                formikProps={formikProps}
                isSavingCode={isSavingCode}
                isEditingDiscountCode={isEditingDiscountCode}
                id={id}
                selectedDiscountCode={selectedDiscountCode}
                prefilledEmail={prefilledEmail}
                isOpenDownloadCSVDialog={isOpenDownloadCSVDialog}
                handleCloseDialog={handleCloseDialog}
                handleCloseDownloadCsvDialog={handleCloseDownloadCsvDialog}
              />
            );
          }}
        </Formik>
      </Dialog>

      <Snackbar
        type={status}
        isOpenSnackbar={open}
        responseSuccessMessage="Success, code created!"
        responseErrorMessage={`Error: ${errorMessage}`}
        onClose={handleCloseSnackBar}
      />
    </>
  );
};

export default DiscountsModal;
